import React, { FC, useRef } from 'react';
import { format, parse } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { LinearProgress, useMediaQuery } from '@mui/material';
import { useDebounce } from '../../../../../hooks/useDebounce';
import { useRepairWorkProgress } from '../../../../../hooks/useRepairWorkProgress';
import { IUserRepairWorks } from '../../../../../models/UserInfo';
import { WheelsIcon } from '../../../../commons/icons/Wheels';
import { TextButton, TextButtonClassNames } from '../../../../commons/buttons/TextButton';
import { handleTechWorkButtonAnalytics } from '../../../../../utils';

interface RepairWorkProps {
  work: IUserRepairWorks;
}

export const RepairWork: FC<RepairWorkProps> = ({ work }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width:767px)');
  const progressPercent = useRepairWorkProgress(work);
  const hurryUpMessages = t('technicalWorks.messages', { returnObjects: true }) as string[];
  const hurryUpMessageIndex = useRef(0);

  const handleHurryUp = useDebounce(() => {
    const message = hurryUpMessages[hurryUpMessageIndex.current];
    enqueueSnackbar('', {
      autoHideDuration: 3000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center',
      },
      content: () => <div className="base-toast">{message}</div>,
    });
    handleTechWorkButtonAnalytics();
    hurryUpMessageIndex.current = (hurryUpMessageIndex.current + 1) % hurryUpMessages.length;
  }, 400);

  return (
    <div className="service-cards-container__repair-works">
      <div className="animated-wheel">
        <WheelsIcon className="icon-wheel" />
      </div>
      <div className="repair-details">
        <div className="repair-details__title">
          {t('technicalWorks.technicalWorksIsBeingCarried')}:{' '}
          {work.fromTime && work.fromDate === work.toDate ? (
            <span>
              {t('paymentHistory.paymentPeriodReceipt', {
                from: work.fromTime,
                to: work.toTime,
              })}
            </span>
          ) : (
            <p>
              {work.fromTime
                ? t('paymentHistory.paymentPeriodReceipt', {
                    from: `${format(parse(work.fromDate, 'yyyy-MM-dd', new Date()), 'dd.MM.yy')} ${work.fromTime}`,
                    to: `${format(parse(work.toDate, 'yyyy-MM-dd', new Date()), 'dd.MM.yy')} ${work.toTime}`,
                  })
                : t('technicalWorks.confirmTimeline')}
            </p>
          )}
        </div>
        <div className="repair-details__action">
          <TextButton
            label={t('technicalWorks.hurryUpMasters')}
            handleClick={handleHurryUp}
            className={TextButtonClassNames.Gray}
          />
        </div>
        {!isMobile && <div className="repair-details__reason">{work.Reason}</div>}
        {!isMobile && progressPercent !== null && (
          <div className="repair-details__progress">
            <LinearProgress variant="determinate" value={progressPercent} />
            <div className="passed-percent">{progressPercent}%</div>
          </div>
        )}
      </div>
      {isMobile && (
        <div className="repair-bottom">
          <div className="repair-bottom__reason">{work.Reason}</div>
          {progressPercent !== null && (
            <div className="repair-bottom__progress">
              <LinearProgress variant="determinate" value={progressPercent} />
              <div className="passed-percent">{progressPercent}%</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
